import { AlertCircle, CircleDollarSign, Loader } from "lucide-react";
import {
    Badge,
    Bold,
    Card,
    Divider,
    DonutChart,
    Flex,
    Icon,
    List,
    ListItem,
    SelectBox,
    SelectBoxItem,
    Text,
    Title,
    Toggle,
    ToggleItem,
} from "@tremor/react";
import { Bars3Icon, ChartPieIcon } from "@heroicons/react/24/outline";
import { RangeType, ReportType } from "graphql/__generated__/globalTypes";

import _ from "lodash";
import { getCompanyName } from "features/users";
import moment from "moment";
import numeral from "numeral";
import { useAppSelector } from "app/hooks";
import { useRevenueByClassOFBusiness } from "hooks/useRevenue";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const valueFormatter = (currency: string) => (number: number) =>
    `${currency} ${numeral(number).format("0.0a")}`;

const COLORS = [
    "#3B82F6", // green-500
    "#10B981", // emerald-500
    "#F59E0B", // amber-500
    "#EF4444", // red-500
    "#8B5CF6", // violet-500
    "#EC4899", // pink-500
    "#14B8A6", // teal-500
    "#F97316", // orange-500
    "#64748B", // slate-500
    "#06B6D4", // cyan-500
];

export default function TotalRevenuePie() {
    const { t } = useTranslation();
    const [selectedView, setSelectedView] = useState("chart");
    const [currency, setCurrency] = useState("USD");
    const [broker, setBroker] = useState("visalre");
    const reinsurer = useAppSelector(getCompanyName);

    const { data, loading, error } = useRevenueByClassOFBusiness({
        args: {
            currency: currency,
            from: "2018-01-01",
            rangeType: RangeType.Monthly,
            re_company_name: reinsurer,
            reportType: ReportType.All,
            to: moment().endOf("year").format("YYYY-MM-DD"),
        },
    });

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    if (loading) {
        return (
            <Card>
                <div className="h-[500px] flex items-center justify-center">
                    <div className="flex flex-col items-center text-gray-500">
                        <Loader className="animate-spin h-8 w-8 text-green-500 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.revenue_pie.loading")}
                        </span>
                    </div>
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <div className="h-[500px] flex items-center justify-center">
                    <div className="flex flex-col items-center text-red-500">
                        <AlertCircle className="h-8 w-8 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.revenue_pie.error")}
                        </span>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <div className="p-6">
                <div className="space-y-6">
                    {/* Header Section */}
                    <Flex
                        justifyContent="justify-between"
                        alignItems="items-center">
                        <div>
                            <Title>{t("dashboard.revenue_pie.title")}</Title>
                            <Text>
                                {t("dashboard.revenue_pie.description")}
                            </Text>
                        </div>
                        <Toggle
                            defaultValue={selectedView}
                            color="gray"
                            handleSelect={setSelectedView}>
                            <ToggleItem value="chart" icon={ChartPieIcon} />
                            <ToggleItem value="list" icon={Bars3Icon} />
                        </Toggle>
                    </Flex>

                    <Divider />

                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Badge
                                icon={CircleDollarSign}
                                color="green"
                                tooltip="Premium income distribution"
                                text={reinsurer || "All Reinsurers"}
                            />
                        </div>

                        <div className="flex gap-3">
                            <SelectBox
                                value={broker}
                                onValueChange={setBroker}
                                placeholder={t(
                                    "dashboard.revenue_pie.select_broker",
                                )}
                                maxWidth="max-w-xl">
                                <SelectBoxItem value="visalre" text="KEK Re" />
                                <SelectBoxItem value="kekre" text="iRisk Re" />
                                <SelectBoxItem
                                    value="iriskre"
                                    text="Visal Re"
                                />
                            </SelectBox>

                            <SelectBox
                                value={currency}
                                onValueChange={setCurrency}
                                placeholder={t(
                                    "dashboard.revenue_pie.select_currency",
                                )}
                                maxWidth="max-w-xl">
                                <SelectBoxItem value="USD" text="USD" />
                                <SelectBoxItem value="EUR" text="EUR" />
                                <SelectBoxItem value="GBP" text="GBP" />
                                <SelectBoxItem value="GHS" text="GHS" />
                            </SelectBox>
                        </div>
                    </div>

                    {/* Content */}
                    {selectedView === "chart" ? (
                        <div className="h-[350px]">
                            <DonutChart
                                data={data}
                                category={broker}
                                height="h-96"
                                dataKey="class_of_business"
                                valueFormatter={valueFormatter(currency)}
                                colors={[
                                    "blue",
                                    "emerald",
                                    "amber",
                                    "red",
                                    "violet",
                                    "pink",
                                    "teal",
                                    "orange",
                                    "slate",
                                    "cyan",
                                ]}
                                showAnimation={true}
                                // animationDuration={1000}
                                marginTop="mt-6"
                                variant="pie"
                            />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <Flex justifyContent="justify-between">
                                <Text>
                                    <Bold>
                                        {t(
                                            "dashboard.revenue_pie.class_of_business",
                                        )}
                                    </Bold>
                                </Text>
                                <Text>
                                    <Bold>
                                        {t(
                                            "dashboard.revenue_pie.premium_amount",
                                        )}
                                    </Bold>
                                </Text>
                            </Flex>

                            <div className="max-h-[300px] overflow-y-auto pr-2">
                                <List>
                                    {data.map((item, index) => (
                                        <ListItem key={item.class_of_business}>
                                            <Flex justifyContent="justify-start">
                                                <div
                                                    className="w-3 h-3 rounded-full mr-2"
                                                    style={{
                                                        backgroundColor:
                                                            COLORS[
                                                                index %
                                                                    COLORS.length
                                                            ],
                                                    }}
                                                />
                                                <Text>
                                                    {item.class_of_business}
                                                </Text>
                                            </Flex>
                                            <Text>
                                                {formatCurrency(
                                                    _.get(item, broker, 0),
                                                )}
                                            </Text>
                                        </ListItem>
                                    ))}
                                </List>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}
